"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useMemo, type CSSProperties } from "react";

/**
 * Program Benefits 5008 — KUK "Why KUK Online" benefit cards.
 *
 * Port of kuk-campus-portal `components/Home/KeyBenefits.tsx`: the grid of
 * benefit cards, each a photograph washed out behind an icon, a title and a line
 * of copy.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * THE CARD WASH — v1 stacks THREE backgrounds on each card: a flat white layer,
 * a flat navy layer, then the photograph. It alternates the two flat layers'
 * opacity by index parity (even cards get white .6 over navy .5, odd cards white
 * .8 over navy .4), so the row reads as a subtle checker rather than six
 * identical tiles. That alternation is the whole visual character of the block,
 * so it is reproduced exactly — both pairs are CMS fields, and which pair a card
 * gets still follows its position.
 *
 * HEADING — v1 rebuilds the heading by hand: it prints the first two words, then
 * the LAST word in the brand colour, then a literal " ?" — so "Why KUK online"
 * renders as "Why KUK online ?" with only "online" coloured. That silently
 * mangles any heading that is not exactly three words, and hard-codes a question
 * mark onto every one. Here the heading is authored as-is and uses this app's
 * `{phrase}` convention for the coloured run, the same as About 5005.
 *
 * DROPPED — v1's `backgroundColor: "lightgray"` under the card image.
 * `lightgray` is not in that project's Tailwind palette and the value is passed
 * as an inline style, so it does paint — but only in the sliver between the
 * image's edge and the card box, which the `cover` sizing already fills. It
 * changes nothing on screen and is not carried over.
 *
 * DROPPED — v1's fixed `w-[300px]` on each card, which fights the `width: 100%`
 * set immediately after it in the same element's inline style. The inline width
 * wins, so the cards are already fluid; only the dead class is dropped.
 */

/** v1: even-index cards. White wash over a navy tint, over the photo. */
const WASH_A = "rgba(255, 255, 255, 0.60)";
const TINT_A = "rgba(3, 31, 68, 0.50)";
/** v1: odd-index cards. */
const WASH_B = "rgba(255, 255, 255, 0.80)";
const TINT_B = "rgba(3, 31, 68, 0.40)";
/** v1: `width="25" height="25"` below `tab`; the desktop icon takes its default. */
const ICON_SIZE_MOBILE = "25";
const ICON_SIZE_DESKTOP = "40";

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface BenefitRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  /** An `iconMap` key (e.g. "clock") or an uploaded icon URL. */
  meta_icon?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_title_color?: string;
  meta_sub_title_color?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits5008Data {
  status?: boolean | number | string;
  title?: string;
  title_start_color?: string;
  /** Colour applied to any `{braced}` run in the heading. */
  title_end_color?: string;
  subtitle?: string;
  subtitle_color?: string;
  desktop_background_color?: string;
  /** Card wash / tint, alternating by card position. */
  gradient_color_1?: string;
  gradient_color_3?: string;
  gradient_color_2?: string;
  gradient_color_4?: string;
  /** Card icon tint (icon-map icons only; uploaded art keeps its own colours). */
  pre_title_color?: string;
  metadata?: BenefitRow[];
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Wrap any `{phrase}` in a coloured span. Mirrors About 5005. */
function highlight(text: string, color: string): string {
  if (!text) return "";
  return escapeHtml(text).replace(
    /\{([^}]+)\}/g,
    `<span style="color:${color}">$1</span>`,
  );
}

/** `url("…")` with the quote that would close it escaped out. */
function toCssUrl(src: string): string {
  return `url("${src.replace(/"/g, "%22")}")`;
}

const ProgramBenefits5008 = (props: { data?: ProgramBenefits5008Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramBenefits5008Data
    | undefined;

  const {
    title = "",
    title_start_color = "#101112",
    title_end_color = "#9F1C33",
    subtitle = "",
    subtitle_color = "#101112",
    desktop_background_color = "#FFFFFF",
    gradient_color_1 = WASH_A,
    gradient_color_3 = TINT_A,
    gradient_color_2 = WASH_B,
    gradient_color_4 = TINT_B,
    pre_title_color = "#031F44",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter(
        (r) => r?.meta_status !== false && (r?.meta_title || r?.meta_sub_title),
      )
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || cards.length === 0) return null;

  return (
    <section
      className="flex w-full items-center justify-center"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="w-full max-w-[1120px] p-10 tab:p-20">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_start_color }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: highlight(title, title_end_color),
              }}
            />
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: title_end_color }}
            />
          </h2>
        ) : null}

        {/* v1 hides the sub-heading below `tab` — the cards carry the message
            there and the extra line pushes them off the first screen. */}
        {subtitle ? (
          <p
            className="hidden py-5 text-lg tracking-wide tab:block"
            style={{ color: subtitle_color }}
          >
            {subtitle}
          </p>
        ) : null}

        <div className="mx-auto grid grid-cols-2 gap-1 lg:grid-cols-3">
          {cards.map((card, index) => {
            const isEven = index % 2 === 0;
            const wash = isEven ? gradient_color_1 : gradient_color_2;
            const tint = isEven ? gradient_color_3 : gradient_color_4;
            const layers = [
              `linear-gradient(0deg, ${wash} 0%, ${wash} 100%)`,
              `linear-gradient(0deg, ${tint} 0%, ${tint} 100%)`,
              card.meta_image ? toCssUrl(card.meta_image) : null,
            ].filter(Boolean) as string[];

            return (
              <div
                key={card.id ?? index}
                className={cn(
                  "relative flex w-full flex-col items-start justify-start gap-1",
                  "bg-cover bg-center bg-no-repeat p-5",
                  "tab:gap-5 tab:p-7 md:h-[300px]",
                )}
                style={{ backgroundImage: layers.join(", ") } as CSSProperties}
                role={card.meta_image ? "img" : undefined}
                aria-label={
                  card.meta_image
                    ? card.meta_alt_image || card.meta_title || undefined
                    : undefined
                }
              >
                {card.meta_icon ? (
                  <>
                    <span className="z-10 hidden tab:block">
                      <DynamicIcon
                        iconName={card.meta_icon}
                        width={ICON_SIZE_DESKTOP}
                        height={ICON_SIZE_DESKTOP}
                        color={pre_title_color}
                      />
                    </span>
                    <span className="z-10 tab:hidden">
                      <DynamicIcon
                        iconName={card.meta_icon}
                        width={ICON_SIZE_MOBILE}
                        height={ICON_SIZE_MOBILE}
                        color={pre_title_color}
                      />
                    </span>
                  </>
                ) : null}

                {card.meta_title ? (
                  <h3
                    className="z-10 text-left text-sm font-bold tab:text-xl"
                    style={{ color: card.meta_title_color || "#101112" }}
                  >
                    {card.meta_title}
                  </h3>
                ) : null}

                {card.meta_sub_title ? (
                  <p
                    className="z-10 text-xs font-light tab:text-sm"
                    style={{ color: card.meta_sub_title_color || "#101112" }}
                  >
                    {card.meta_sub_title}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramBenefits5008;
